import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils & misc
import cn from '../../../utils/cn';
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';

// Style
import style from './MenuNavigationButton.module.css';

/**
 * MenuNavigationButton component - A navigation button with icon, label and states
 * @param {object} params
 * @param {string} params.id - Id of the button
 * @param {string} [params.href] - Href of the button
 * @param {(event: React.MouseEvent<HTMLAnchorElement>) => void} params.onClick - Click handler function
 * @param {string | null} [params.icon=null] - Icon name from available pictos
 * @param {string} params.label - Button text label
 * @param {boolean} [params.isActive=false] - Whether the button is in active state
 * @param {boolean} [params.isMenuOpen=false] - Whether the menu is open
 * @param {string} [params.className] - Additional CSS classes
 * @param {React.ReactNode | null} [params.children] - Children elements
 * @param {'default' | 'submenu'} [params.theme='default'] - Theme of the button
 * @param {boolean} [params.isDisabled=false] - Not navigable: clicks ignored, out of tab order
 * @param {string} [params.title] - Tooltip, defaults to the label
 * @returns {JSX.Element} MenuNavigationButton component
 */
function MenuNavigationButton({
  id,
  href,
  onClick,
  icon = null,
  label,
  isActive = false,
  isMenuOpen = false,
  className,
  children = null,
  theme = 'default',
  isDisabled = false,
  title,
}) {
  const iconDefinition = parseFontAwesomeClass(icon);

  return (
    <a
      id={id}
      href={href}
      className={cn([
        style['menu-navigation-button'],
        style[theme],
        isActive && style.active,
        isMenuOpen && style['menu-open'],
        isDisabled && style.disabled,
        className,
      ])}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        onClick?.(e);
      }}
      // Middle click would open the href in a new tab
      onAuxClick={isDisabled ? (e) => e.preventDefault() : undefined}
      tabIndex={isMenuOpen && !isDisabled ? 0 : -1}
      aria-disabled={isDisabled || undefined}
      title={title ?? label}
      aria-label={label}
    >
      <div className={style.content}>
        {iconDefinition && theme === 'default' ? (
          <div className={cn([style.icon])}>
            <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} />
          </div>
        ) : null}
        <span className={cn([style.label])}>
          {label}
        </span>
      </div>

      {children}
    </a>
  );
}

export default MenuNavigationButton;
